import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isEmpty, isNil } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
// Import Types
import type { BudgetHistoryListItem, CampaignBudget } from 'types';
// Import UI Components
import { Empty } from '@geneui/components';
// Import Components
import DataTable from 'components/common/DataTable';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
import { useListFilters } from 'hooks/useListFilters';
import { usePartnerDateFormats } from 'hooks/UsePartnerDateFormats';
import { useDataTableColumnsConfigs } from './hooks';
// Import Helpers
import { formatNumber } from 'services/Helpers';
// Import Services
import { BudgetHttpService } from 'services/http';
// Import Enums
import { BudgetLimitTypeEnum, FormatNumberActionEnum, PromotionTypeEnum } from 'enums';
// Import Constants
import { l } from 'constants/common';
import { BUDGET_BEHAVIOR_LABEL_KEYS, BUDGET_PERIOD_LABEL_KEYS } from 'constants/budget';
import { campaignBudgetHistoryInitialState } from 'constants/listDefaultFilters';
// Import Styles
import styles from './campaignReportBudgetLimitation.module.scss';

interface Props {
    campaignId: string;
    budget: Nilable<CampaignBudget>;
}

const CampaignReportBudgetLimitation: React.FC<Props> = ({ campaignId, budget }) => {
    const { LimitType, LimitValue, CurrencyId, Behavior, Period } = budget ?? {};
    const { t } = useTranslation();
    const { doPostRequest } = useRequest();
    const { partnerDateTimeFormat } = usePartnerDateFormats();

    const [isBudgetHistoryListLoading, setIsBudgetHistoryListLoading] = useState(true);
    const [budgetHistoryList, setBudgetHistoryList] = useState<BudgetHistoryListItem[]>([]);
    const [budgetHistoryCount, setBudgetHistoryCount] = useState(0);

    const { columns } = useDataTableColumnsConfigs(t, partnerDateTimeFormat);

    const { getBudgetHistoryListRequest } = useCancelableRequests(
        { getBudgetHistoryListRequest: BudgetHttpService.getBudgetHistoryList },
        { takeLatest: ['getBudgetHistoryListRequest'] },
    );

    const { baseRequestBody, changePageNumber, changePageSize, changeSorting } = useListFilters(
        campaignBudgetHistoryInitialState,
    );

    const tableRows = useMemo(() => {
        if (isEmpty(budgetHistoryList)) return [];

        return budgetHistoryList.map((data, index) => {
            const uniqId = uuidv4();
            return {
                rowKey: uniqId,
                hasHover: true,
                dragDisable: true,
                data: {
                    index,
                    Id: uniqId,
                    ...data,
                },
            };
        });
    }, [budgetHistoryList]);

    const init = () => {
        setIsBudgetHistoryListLoading(true);
        doPostRequest(getBudgetHistoryListRequest.request, {
            queryString: {
                promotionId: campaignId,
                promotionType: PromotionTypeEnum.Campaign,
                withTotalCount: true,
            },
            requestBody: baseRequestBody,
            successCallback: ({ Data, Count }) => {
                setBudgetHistoryList(Data);
                setBudgetHistoryCount(Count);
            },
        }).then(({ IsCanceled }) => {
            // A superseded call must not hide the loader of the call that replaced it
            if (!IsCanceled) {
                setIsBudgetHistoryListLoading(false);
            }
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, [baseRequestBody]);

    return (
        <div className={styles.container}>
            {budget ? (
                <>
                    <SectionWithTitle title={t(l.GeneralInfo)} hasContentPadding={false} hasTitleBorder={false}>
                        <div className="general-details-content">
                            <div className="general-details-column">
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.BudgetPeriod)}</p>
                                    <p className="g-details-subtitle">
                                        {t(!isNil(Period) ? (BUDGET_PERIOD_LABEL_KEYS[Period] ?? l.NA) : l.NA)}
                                    </p>
                                </div>
                                <div className="general-details-column-info">
                                    <p className="g-details-title">
                                        {t(LimitType === BudgetLimitTypeEnum.Amount ? l.Amount : l.Count)}
                                    </p>
                                    <p className="g-details-subtitle">
                                        {isNil(LimitValue)
                                            ? t(l.NA)
                                            : formatNumber(LimitValue, {
                                                  type: FormatNumberActionEnum.DecimalPrecision,
                                                  precision: 2,
                                                  isClean: true,
                                              })}
                                    </p>
                                </div>
                            </div>
                            <div className="general-details-column">
                                <div className="general-details-column-info">
                                    <p className="g-details-title">{t(l.LimitBehavior)}</p>
                                    <p className="g-details-subtitle">
                                        {t(!isNil(Behavior) ? (BUDGET_BEHAVIOR_LABEL_KEYS[Behavior] ?? l.NA) : l.NA)}
                                    </p>
                                </div>
                                {!isNil(CurrencyId) && (
                                    <div className="general-details-column-info">
                                        <p className="g-details-title">{t(l.Currency)}</p>
                                        <p className="g-details-subtitle">{CurrencyId}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </SectionWithTitle>

                    <SectionWithTitle
                        title={t(l.BudgetInfo)}
                        hasContentPadding={false}
                        hasTitleBorder={false}
                        hasFullHeight={true}
                        contentClassName={styles.budgetInfoContainer}
                    >
                        <DataTable
                            rowKey="Id"
                            columnKey="dataKey"
                            classNames={styles.budgetInfoTable}
                            name={null}
                            data={tableRows}
                            dataCount={budgetHistoryCount}
                            columns={columns}
                            isLoading={isBudgetHistoryListLoading}
                            isHasLeftHeaderActions={false}
                            withHeaderActions={false}
                            withManageLabels={false}
                            withSearch={false}
                            isVisibleHeader={false}
                            isColumnsSortable={true}
                            withPagination={true}
                            isRowExpandable={false}
                            isShowColumnChooser={false}
                            isShowVerticalDots={false}
                            rowActionBar={null}
                            sorting={baseRequestBody.Sorting}
                            setSorting={changeSorting}
                            setPagingPageSize={changePageSize}
                            setPagingPageNumber={changePageNumber}
                            receivedCurrentPageNumber={baseRequestBody.Pageing.PageNumber}
                            withQueryParams={false}
                        />
                    </SectionWithTitle>
                </>
            ) : (
                <Empty />
            )}
        </div>
    );
};

export default memo(CampaignReportBudgetLimitation);
